import { getCurrentUser, getUserDetails } from "../../../services/authService";
import { STORAGE_KEYS } from "../../../constants/storage";

export const OAUTH2_AUTH_TYPE = "oauth2_client_credentials";

/** Decode Orbit JWT payload (role lives here after password login). */
export function getJwtPayload() {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** Role from JWT claims first, then stored user details (see ui_integration.md). */
export function getUserRole() {
  const claims = getJwtPayload();
  if (claims?.role) return String(claims.role).toLowerCase();

  const details = getUserDetails();
  if (details?.role) return String(details.role).toLowerCase();

  const user = getCurrentUser();
  if (user?.role) return String(user.role).toLowerCase();

  return "";
}

export function isAdminUser() {
  return getUserRole() === "admin";
}

export const FALLBACK_AUTH_TYPES = [
  {
    value: "none",
    label: "None",
    description: "Plain POST with no auth header.",
    required_auth_config_fields: []
  },
  {
    value: OAUTH2_AUTH_TYPE,
    label: "OAuth2 Client Credentials",
    description: "Fetch a Bearer token before each dispatch.",
    required_auth_config_fields: ["token_url", "scopes", "client_id", "client_secret"]
  }
];

export function resolveAuthTypes(integrations) {
  const fromApi = integrations?.auth_types;
  if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi;
  return FALLBACK_AUTH_TYPES;
}

export function isAuthedWebhook(webhook) {
  const t = webhook?.auth_type;
  return t && t !== "none";
}

export function authBadge(webhook) {
  if (isAuthedWebhook(webhook)) {
    return {
      label: "🔒 OAuth2",
      className: "bg-amber-50 text-amber-800 border border-amber-200"
    };
  }
  return {
    label: "No auth",
    className: "bg-gray-100 text-gray-600 border border-gray-200"
  };
}

export function statusBadge(isActive) {
  return isActive
    ? { label: "Active", className: "bg-green-50 text-green-700" }
    : { label: "Disabled", className: "bg-gray-100 text-gray-600" };
}

export const AUTH_FIELD_META = {
  token_url: {
    label: "Token URL",
    placeholder: "https://example.com/oauth/token",
    hint: "OAuth2 token endpoint (e.g. /connect/token)"
  },
  scopes: {
    label: "Scopes",
    placeholder: "scope.one scope.two",
    hint: "Space-separated"
  },
  client_id: {
    label: "Client ID",
    placeholder: "Provided by the destination service"
  },
  client_secret: { label: "Client Secret", placeholder: "", secret: true }
};

export function emptyOAuthConfig() {
  return { token_url: "", scopes: "", client_id: "", client_secret: "" };
}

export function buildOAuthConfigFromForm(form, { includeSecrets = true } = {}) {
  const cfg = {
    token_url: form.token_url?.trim() ?? "",
    scopes: form.scopes?.trim() ?? ""
  };
  if (includeSecrets) {
    if (form.client_id?.trim()) cfg.client_id = form.client_id.trim();
    if (form.client_secret?.trim()) cfg.client_secret = form.client_secret.trim();
  }
  return cfg;
}

export function validateOAuthConfig(form, requiredFields) {
  const errors = {};
  for (const field of requiredFields || []) {
    if (field === "client_id" || field === "client_secret") {
      if (!form[field]?.trim()) errors[field] = "Required";
      continue;
    }
    if (!form[field]?.trim()) errors[field] = "Required";
  }
  return errors;
}

/** Map 422 validation messages to form fields where possible */
export function parseValidationErrors(error) {
  const detail = error?.response?.data?.detail;
  const errors = {};
  const messages = [];

  if (Array.isArray(detail)) {
    for (const item of detail) {
      const msg = item.msg || String(item);
      messages.push(msg);
      const lower = msg.toLowerCase();
      if (lower.includes("client_secret")) errors.client_secret = msg;
      else if (lower.includes("client_id")) errors.client_id = msg;
      else if (lower.includes("token_url")) errors.token_url = msg;
      else if (lower.includes("scopes")) errors.scopes = msg;
      else if (lower.includes("webhook_url")) errors.webhook_url = msg;
      else if (lower.includes("name")) errors.name = msg;
      else if (lower.includes("auth_type") || lower.includes("auth_config")) {
        errors.auth_type = msg;
      }
    }
  } else if (typeof detail === "string") {
    messages.push(detail);
  }

  return { fieldErrors: errors, messages };
}

export function formatApiError(error, fallback = "Request failed") {
  const { messages } = parseValidationErrors(error);
  if (messages.length) return messages.join("; ");
  const d = error?.response?.data;
  if (d?.detail !== undefined && typeof d.detail === "string") return d.detail;
  if (error?.message) return error.message;
  return fallback;
}
